import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { UserState } from '@trustero/trustero-api-web/lib/account/account_pb'
import { useUsers } from '../../../../async/Users'
import { FilterName, FilterParam } from '../FilterBar.types'
import { Owner } from '../../../../../Utils/globalEnums'
import { FilterDropdown } from './FilterDropdown'
import { DropdownItemType } from './FilterDropdown.constants'

export type OwnerGroupOption = { value: string; label: string }
const NO_GROUPS: OwnerGroupOption[] = []
const NO_CAPABILITIES: never[] = []

export const OwnerFilterDropdown = React.memo(
  ({
    customName,
    includeUnassigned = true,
    isNested = false,
    filterParam = FilterParam.OWNER,
    filterName = FilterName.OWNER,
    groups = NO_GROUPS,
  }: {
    customName?: string
    includeUnassigned?: boolean
    isNested?: boolean
    filterParam?: FilterParam
    filterName?: FilterName
    // Assignable user groups, listed under their own heading after the users. Their
    // values are passed through to the URL as given.
    groups?: OwnerGroupOption[]
  }): JSX.Element => {
    const location = useLocation()
    // Requests filter by user id; the other pages by lower-cased email.
    const isRequestsPage = location.pathname.includes('requests')
    const userResp = useUsers()

    const filterValues = useMemo<DropdownItemType[]>(() => {
      const users =
        userResp.data
          ?.getUsersList()
          .filter((user) => user.getState() === UserState.USER_ACTIVE) ?? []
      const userValues: DropdownItemType[] = [
        ...(includeUnassigned
          ? [{ value: Owner.UNASSIGNED_BE, label: Owner.UNASSIGNED }]
          : []),
        ...users.map((user) => ({
          value: isRequestsPage ? user.getId() : user.getEmail().toLowerCase(),
          label: user.getName() || user.getEmail(),
        })),
      ]
      if (!groups.length) return userValues
      return [
        { value: 'owner-heading-users', label: 'Users', isGroupHeading: true },
        ...userValues,
        {
          value: 'owner-heading-groups',
          label: 'Groups',
          isGroupHeading: true,
        },
        ...groups,
      ]
    }, [userResp.data, includeUnassigned, isRequestsPage, groups])

    if (userResp.error) return <></>

    return (
      <FilterDropdown
        filterParam={filterParam}
        filterName={(customName as FilterName) || filterName}
        filterValues={filterValues}
        isLoading={userResp.isLoading}
        isNested={isNested}
        // Groups can be picked by someone who cannot list users.
        requiredCapabilities={groups.length ? NO_CAPABILITIES : undefined}
      />
    )
  },
)
